import { APITypesV1 } from "@cur8/api-client";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useAssignSkinScannerMutation } from "hooks/mutations/useAssignSkinScannerMutation";
import { useBloodWorkModeQuery } from "hooks/queries/useBloodWorkModeQuery";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useSkinScannerStatusQuery } from "hooks/queries/useSkinScannerStatus";
import { useVisitBloodSamplesQuery } from "hooks/queries/useVisitBloodSamplesQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { readScanStateFromVisit } from "lib/readScanStateFromVisit";
import { everyLimsTubeRecorded, hasLimsManagedSamples } from "lib/tubeStatus";
import { CheckInParams } from "lib/types/checkInParameters";
import { useCallback, useEffect, useMemo } from "react";
import Checkmark from "render/assets/icons/96x96_check.svg?react";
import { useAppInsights } from "render/context/AppInsightsContext";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { List } from "render/ui/presentation/List";
import { SpinningLoader } from "render/ui/presentation/SpinningLoader";
import { Link } from "render/ui/trigger/Link";
import { isBloodWorkComplete } from "render/views/AssignVials/scanDecision";
import { formatAddressableName } from "lib/formatMemberName";
import { BailView } from "render/views/BailView";
import {
  bloodModelsCompete,
  nextDestinationAfterSkinScanner,
  SkinScannerRouting,
} from "./nextDestination";
import styles from "./styles.module.sass";

export function AssignToSkinScanner({
  goTo,
  patientId,
  visitId,
}: AssignToSkinScannerProps) {
  const { data: patient } = usePatientQuery({ patientId });
  const { data: visit } = useVisitQuery({ patientId, visitId });
  const assignSkinScannerMutation = useAssignSkinScannerMutation();
  const skinScannerStatusQuery = useSkinScannerStatusQuery({
    patientId: patientId,
    visitId: visitId,
  });
  const bloodWorkModeQuery = useBloodWorkModeQuery({ patientId, visitId });
  const bloodSamplesQuery = useVisitBloodSamplesQuery({ patientId, visitId });
  const { data: featurePreferences } = useFeaturePreference();

  const appInsights = useAppInsights();

  const routing = useMemo(
    (): SkinScannerRouting => ({
      patientId,
      visitId,
      bloodWorkMode: bloodWorkModeQuery.data?.bloodWorkMode,
      bloodWorkAlreadyComplete: isBloodWorkComplete(
        bloodSamplesQuery.data?.bloodSamples
      ),
      showBloodDrawStep:
        featurePreferences.bloodDrawStepEnabled &&
        hasLimsManagedSamples(bloodSamplesQuery.data?.bloodSamples),
      limsBloodDrawAlreadyRecorded: everyLimsTubeRecorded(
        bloodSamplesQuery.data?.bloodSamples
      ),
    }),
    [
      patientId,
      visitId,
      bloodWorkModeQuery.data?.bloodWorkMode,
      bloodSamplesQuery.data?.bloodSamples,
      featurePreferences.bloodDrawStepEnabled,
    ]
  );

  // The destination is read at navigation time, so navigating before these
  // answer routes on absent data and skips the blood draw step. isFetched, not
  // isSuccess: an errored query still has to let the operator move on.
  const routingReady =
    bloodWorkModeQuery.isFetched && bloodSamplesQuery.isFetched;

  // Every way forward off this screen. An errored samples query reads as "no
  // LimsManaged tubes", so it routes past the blood draw step: the scan
  // progress tile is the backstop once a refetch succeeds, and this records how
  // often we lean on it.
  const goOnward = useCallback(() => {
    if (bloodSamplesQuery.isError) {
      appInsights.trackException({
        exception: new Error(
          "Routed past the skin scanner with an errored blood samples query"
        ),
        properties: { patientId, visitId },
      });
    }
    goTo(nextDestinationAfterSkinScanner(routing));
  }, [
    appInsights,
    bloodSamplesQuery.isError,
    goTo,
    patientId,
    routing,
    visitId,
  ]);

  // Both blood models on one visit means vial assignment quietly takes the slot
  // the blood draw step wanted. Not believed reachable, so find out for sure.
  useEffect(() => {
    if (!bloodModelsCompete(routing)) {
      return;
    }
    appInsights.trackException({
      exception: new Error(
        "Visit has both an outstanding vial assignment and LimsManaged samples"
      ),
      properties: { patientId, visitId },
    });
  }, [appInsights, patientId, routing, visitId]);

  const skinScannerStatus = useMemo(() => {
    return skinScannerStatusQuery.data?.status;
  }, [skinScannerStatusQuery.data?.status]);
  const statusLabel = skinScannerStatus
    ? statusLabels[skinScannerStatus]
    : "Could not get status";
  const canAssign = useMemo(() => {
    return (
      skinScannerStatus &&
      skinScannerStatus !==
        APITypesV1.DeviceAssignmentStatus.OtherMemberAssigned
    );
  }, [skinScannerStatus]);

  const skinScan = readScanStateFromVisit("skin", visit);
  const alreadyHasSkinScan = skinScan != null;

  const loading = assignSkinScannerMutation.isPending;
  const error = assignSkinScannerMutation.isError;
  const success = assignSkinScannerMutation.isSuccess;

  if (!patient) {
    return <BailView title="Could not fetch member" />;
  }

  const memberName = formatAddressableName(patient.name);
  const text = alreadyHasSkinScan
    ? `Are you sure you want to reassign ${memberName} to the skin scanner?`
    : `Assign ${memberName} to the skin scanner when they are ready`;
  const buttonText = alreadyHasSkinScan
    ? "Reassign to skin scanner"
    : "Assign to skin scanner";

  return (
    <PageFrameContent>
      <section className={styles.base}>
        <section
          className={styles.transition}
          data-active={!loading && !error && !success}
          aria-hidden={loading || error || success}
        >
          <InformationLayout
            content={
              <section className={styles.layout}>
                <div className={styles.text}>
                  <Typography variant="display-s">{text}</Typography>
                  {alreadyHasSkinScan ? (
                    <Typography variant="title-s" color="subtle">
                      {memberName} already had a skin scan today.
                    </Typography>
                  ) : null}
                </div>
                <List>
                  <List.Item>
                    <div className={styles.item}>
                      <Typography variant="title-m">Skin scanner</Typography>
                      {statusLabel ? (
                        <StatusBadge error={!canAssign} label={statusLabel} />
                      ) : undefined}
                    </div>
                  </List.Item>
                </List>
              </section>
            }
            buttons={
              <Button
                state={!canAssign || !routingReady ? "disabled" : undefined}
                onPress={() => {
                  assignSkinScannerMutation.mutate(
                    {
                      patientId: patientId,
                      visitId: visitId,
                    },
                    { onSuccess: () => setTimeout(goOnward, 3000) }
                  );
                }}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                {buttonText}
              </Button>
            }
          />
          <Link
            className={styles.skipLink}
            isDisabled={!routingReady}
            onPress={goOnward}
          >
            Skip skin scan
          </Link>
        </section>
        <section
          className={styles.transition}
          data-active={loading || success}
          aria-hidden={!loading && !success}
        >
          <div className={styles.loader}>
            <SpinningLoader success={success} />
            <div className={styles.loaderText} data-active={success}>
              <Typography variant="title-s">Member assigned</Typography>
              <Typography variant="title-s">to skin scanner</Typography>
            </div>
            <div className={styles.checkmark} data-active={success}>
              <Checkmark />
            </div>
          </div>
        </section>
        <section
          className={styles.transition}
          data-active={error}
          data-bottom
          aria-hidden={!error}
        >
          <InformationLayout
            content={
              <section className={styles.title}>
                <Typography variant="display-s">
                  Couldn't assign member to the skin scanner
                </Typography>
                <Typography variant="body-m" color="subtle">
                  Please try again, or cancel and proceed with the other
                  examinations
                </Typography>
              </section>
            }
            buttons={
              <ActionsFooter
                left={
                  <Button
                    variant="secondary"
                    onPress={() => {
                      goTo(paths.checkInSuccess.url({ patientId, visitId }));
                    }}
                    fillContainer
                  >
                    Cancel
                  </Button>
                }
                right={
                  <Button
                    state={
                      loading
                        ? "loading"
                        : !routingReady
                          ? "disabled"
                          : undefined
                    }
                    onPress={() => {
                      assignSkinScannerMutation.mutate(
                        {
                          patientId: patientId,
                          visitId: visitId,
                        },
                        { onSuccess: () => setTimeout(goOnward, 3000) }
                      );
                    }}
                    fillContainer
                    icon={<Icon24ArrowRight />}
                  >
                    Assign to skin scanner
                  </Button>
                }
              />
            }
          />
        </section>
      </section>
    </PageFrameContent>
  );
}

type AssignToSkinScannerProps = {
  goTo(url: string): void;
} & CheckInParams;

const statusLabels = {
  [APITypesV1.DeviceAssignmentStatus.CanAssign]: "Free",
  [APITypesV1.DeviceAssignmentStatus.AssignedToMember]: "Already assigned",
  [APITypesV1.DeviceAssignmentStatus.OtherMemberAssigned]: "Occupied",
} as const;

function StatusBadge({ error, label }: StatusBadgeProps) {
  return (
    <div className={styles.status} data-error={error}>
      <Typography variant="eyebrow-m" as="span">
        {label}
      </Typography>
    </div>
  );
}

type StatusBadgeProps = {
  error: boolean | undefined;
  label: string;
};
